/**
 * CleanerSpecialtyBoard — Iter-178 · Phase 7.
 *
 * Cleaner-only specialty services board. Lets a Cleaners agent submit
 * a specialty service request pre-populated with their currently
 * assigned facility. On submission the request lands in the Operations
 * Command Center queue with status "Pending Completion". Once the
 * cleaner marks the request complete they attach photos inline —
 * the completed record syncs back automatically.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Sparkles, Check, X, CalendarDays, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { SPECIALTY_LABELS, SPECIALTY_DESCRIPTIONS } from '../../lib/specialtyServices';
import InlinePhotoAttachments from '../InlinePhotoAttachments';

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICE_SLUGS = Object.keys(SPECIALTY_LABELS);

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function CleanerSpecialtyBoard({ user }) {
  const [selected, setSelected] = useState([]);
  const [dates, setDates] = useState([todayISO()]);
  const [instructions, setInstructions] = useState('');
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  const facility = user?.property_name || user?.assigned_property_name || 'Assigned facility';
  const facilityId = user?.property_id || user?.assigned_property_id;

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/service-requests`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        const mine = data.filter((x) =>
          SERVICE_SLUGS.includes(x.request_type) && x.submitted_by === user?.user_id);
        setRows(mine);
      }
    } catch (_) { /* silent */ }
  }, [user?.user_id]);

  useEffect(() => { load(); }, [load]);

  const toggle = (slug) => setSelected((p) =>
    p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]);

  const toggleDate = (iso) => setDates((p) =>
    p.includes(iso) ? p.filter((d) => d !== iso) : [...p, iso].sort());

  const canSubmit = selected.length > 0 && dates.length > 0 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const suffix = ` [Scheduled: ${dates.join(', ')}]`;
      const results = await Promise.all(selected.map((slug) => fetch(
        `${API}/api/service-requests`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            request_type: slug,
            description: (instructions.trim() || `Specialty service — ${SPECIALTY_LABELS[slug]}`) + suffix,
            priority: 'normal',
            property_id: facilityId,
            requested_dates: dates,
            source: 'cleaner_specialty',
            status: 'pending_completion',
          }),
        },
      ).then((r) => r.ok)));
      if (results.every(Boolean)) {
        toast.success(`Submitted ${selected.length} specialty request${selected.length > 1 ? 's' : ''} for ${facility}.`);
        setSelected([]); setDates([todayISO()]); setInstructions('');
        load();
      } else {
        toast.error('Some requests failed. Please retry.');
      }
    } catch (_) { toast.error('Network error'); }
    finally { setBusy(false); }
  };

  const markComplete = async (id) => {
    try {
      const r = await fetch(`${API}/api/service-requests/${id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed',
                               completed_at: new Date().toISOString() }),
      });
      if (r.ok) {
        toast.success('Marked complete. Attach photos to finalize.');
        load();
      } else {
        toast.error('Could not mark complete');
      }
    } catch (_) { toast.error('Network error'); }
  };

  const grid = useMemo(() => SERVICE_SLUGS.map((s) => ({
    slug: s, label: SPECIALTY_LABELS[s], desc: SPECIALTY_DESCRIPTIONS[s] || '',
  })), []);

  return (
    <div className="space-y-4" data-testid="cleaner-specialty-board">
      <Card className="p-6 border-2 border-[#C9A962] bg-[#141414]">
        <h3 className="flex items-center gap-2 text-lg font-bold text-white mb-1"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Sparkles className="h-5 w-5 text-[#C9A962]" />
          SPECIALTY SERVICES · CLEANERS
        </h3>
        <p className="text-xs text-zinc-400 mb-4">
          Facility pre-filled: <b className="text-white" data-testid="cleaner-specialty-facility">{facility}</b>.
          Submit a specialty request; Operations sees it as Pending Completion.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2"
               data-testid="cleaner-specialty-grid">
            {grid.map((s) => {
              const on = selected.includes(s.slug);
              return (
                <button
                  type="button"
                  key={s.slug}
                  onClick={() => toggle(s.slug)}
                  className={`text-left p-3 pl-9 rounded border-2 relative transition ${
                    on ? 'border-[#C9A962] bg-[#0a0a0a]' : 'border-zinc-800 bg-[#0a0a0a] hover:border-[#C9A962]/60'
                  }`}
                  data-testid={`cleaner-specialty-tile-${s.slug}`}
                  data-checked={on}
                >
                  <span className={`absolute left-2.5 top-3 flex items-center justify-center w-5 h-5 rounded border-2 ${
                    on ? 'bg-[#C9A962] border-[#C9A962]' : 'bg-transparent border-zinc-600'
                  }`}>
                    {on && <Check className="h-3.5 w-3.5 text-black" strokeWidth={3} />}
                  </span>
                  <p className={`text-sm font-bold ${on ? 'text-[#C9A962]' : 'text-white'}`}>{s.label}</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">{s.desc}</p>
                </button>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5"
                 data-testid="cleaner-specialty-selected">
              {selected.map((s) => (
                <span key={s}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold bg-[#C9A962] text-black">
                  {SPECIALTY_LABELS[s]}
                  <button type="button" onClick={() => toggle(s)}
                          className="ml-1 rounded-full hover:bg-black/10"
                          aria-label="Remove"
                          data-testid={`cleaner-specialty-remove-${s}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div>
            <div className="text-xs font-bold text-white flex items-center gap-2 mb-1">
              <CalendarDays className="h-3.5 w-3.5 text-[#C9A962]" /> Preferred date(s)
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 14 }, (_, i) => {
                const d = new Date();
                d.setDate(d.getDate() + i);
                const iso = d.toISOString().slice(0, 10);
                const on = dates.includes(iso);
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => toggleDate(iso)}
                    className={`px-2 py-1 rounded text-[11px] font-bold border ${
                      on ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'bg-transparent text-white border-zinc-700 hover:border-[#C9A962]/60'
                    }`}
                    data-testid={`cleaner-specialty-date-${iso}`}
                    data-active={on || undefined}
                  >
                    {d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label htmlFor="cleaner-specialty-notes"
                   className="text-xs font-bold text-white uppercase tracking-widest">
              Additional Instructions
            </label>
            <Textarea
              id="cleaner-specialty-notes"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              placeholder="Access instructions, staging, preferences, contact person…"
              className="mt-1 bg-black text-white border-zinc-800"
              data-testid="cleaner-specialty-notes"
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold disabled:opacity-50"
              data-testid="cleaner-specialty-submit"
            >
              {busy ? 'Submitting…' : `Submit ${selected.length > 1 ? `${selected.length} requests` : 'request'}`}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-5 bg-[#141414] border border-zinc-800">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-3"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <ClipboardList className="h-4 w-4 text-zinc-400" />
          MY SPECIALTY QUEUE · {rows.length}
        </h3>
        {rows.length === 0 ? (
          <p className="text-xs italic text-zinc-500">No specialty requests submitted yet.</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => {
              const ex = expanded === r.request_id;
              const done = r.status === 'completed';
              return (
                <li key={r.request_id}
                    className="rounded border border-zinc-800"
                    data-testid={`cleaner-specialty-row-${r.request_id}`}>
                  <div className="flex items-center justify-between p-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white">
                        {SPECIALTY_LABELS[r.request_type] || r.request_type}
                      </p>
                      <p className="text-[11px] text-zinc-400 truncate">{r.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] font-bold ${
                        done ? 'bg-emerald-600' : 'bg-amber-600 text-black'
                      }`}>
                        {done ? 'COMPLETED' : 'PENDING COMPLETION'}
                      </Badge>
                      {!done && (
                        <Button
                          type="button" size="sm"
                          onClick={() => markComplete(r.request_id)}
                          className="bg-[#C9A962] text-black hover:bg-[#B8974E] text-xs font-bold h-7 px-2"
                          data-testid={`cleaner-specialty-complete-${r.request_id}`}
                        >
                          Mark Complete
                        </Button>
                      )}
                      <button
                        type="button"
                        onClick={() => setExpanded(ex ? null : r.request_id)}
                        className="text-xs font-bold underline text-[#C9A962]"
                        data-testid={`cleaner-specialty-toggle-${r.request_id}`}
                      >
                        {ex ? 'Hide' : 'Photos'}
                      </button>
                    </div>
                  </div>
                  {ex && (
                    <div className="border-t border-zinc-800 p-3 bg-black">
                      <InlinePhotoAttachments
                        parentKind="service_request"
                        parentId={r.request_id}
                        required
                        testIdPrefix={`cleaner-sr-${r.request_id}`}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
